import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import SEO from '@/components/SEO';

const STRENGTH_LABEL = {
  preliminary: 'Preliminary',
  emerging: 'Emerging',
  moderate: 'Moderate',
  strong: 'Strong',
};

export const ClinicalEvidenceList = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get('/content/clinical-evidence').then(({ data }) => setItems(data)); }, []);
  return (
    <>
      <SEO title="Clinical Evidence Library" description="Traditional Ayurvedic use meets modern research. Educational summaries only." />
      <div className="bg-sans_bone">
        <section className="sans-section">
          <div className="sans-container">
            <div className="sans-label mb-6">Educational only</div>
            <h1 className="sans-h1">Clinical Evidence<br/>Library.</h1>
            <p className="sans-body mt-8 max-w-xl">
              Traditional Ayurvedic use meets modern research. Written to inform, not to prescribe.
              Every entry is educational — outcomes are not guaranteed.
            </p>
            <div className="mt-16 grid md:grid-cols-2 gap-8">
              {items.map((e) => (
                <Link key={e.slug} to={`/clinical-evidence/${e.slug}`} className="group border border-sans_line bg-white p-8 hover:shadow-[0_8px_32px_rgba(0,0,0,0.04)] transition-shadow">
                  <div className="flex items-start justify-between gap-6">
                    <div>
                      <div className="sans-label mb-3">{STRENGTH_LABEL[e.evidence_strength] || e.evidence_strength}</div>
                      <div className="font-serif text-2xl md:text-3xl text-sans_ink">{e.ingredient_name}</div>
                      <div className="text-sm text-sans_mute mt-1 italic">{e.latin_name}</div>
                      <p className="mt-4 text-sm text-sans_mute leading-relaxed">{e.simple_summary}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            {items.length === 0 && <div className="mt-20 text-sans_mute">Coming soon.</div>}
          </div>
        </section>
      </div>
    </>
  );
};

export const ClinicalEvidenceDetail = () => {
  const { slug } = useParams();
  const [e, setE] = useState(null);
  useEffect(() => { api.get(`/content/clinical-evidence/${slug}`).then(({ data }) => setE(data)); }, [slug]);
  if (!e) return <div className="sans-container sans-section text-sans_mute">Loading…</div>;
  return (
    <>
      <SEO title={`${e.ingredient_name} — Clinical Evidence`} description={e.simple_summary} image={e.cover_image} type="article" />
      <div className="bg-sans_bone">
        <section className="sans-section">
          <div className="sans-container max-w-4xl">
            <Link to="/clinical-evidence" className="sans-label hover:text-sans_ink">← Clinical Evidence</Link>
            <div className="sans-label mt-8 mb-4">Evidence strength · {STRENGTH_LABEL[e.evidence_strength]}</div>
            <h1 className="sans-h1">{e.ingredient_name}</h1>
            <div className="text-lg text-sans_mute mt-2 italic font-serif">{e.latin_name}</div>
            <div className="sans-rule mt-8" />

            {e.cover_image && (
              <div className="mt-12 aspect-[16/9] bg-sans_line overflow-hidden">
                <img src={e.cover_image} alt={e.ingredient_name} className="w-full h-full object-cover" />
              </div>
            )}

            <div className="mt-12 grid md:grid-cols-2 gap-12">
              <div>
                <div className="sans-label mb-3">Traditional Ayurvedic use</div>
                <p className="text-sans_ink leading-relaxed">{e.traditional_use}</p>
              </div>
              <div>
                <div className="sans-label mb-3">Modern research summary</div>
                <p className="text-sans_ink leading-relaxed">{e.modern_research}</p>
              </div>
            </div>

            <div className="mt-14">
              <div className="sans-label mb-3">Findings</div>
              <p className="text-sans_ink leading-relaxed">{e.findings}</p>
              {e.typical_study_duration && (
                <div className="mt-4 text-sm text-sans_mute">Typical study duration: {e.typical_study_duration}</div>
              )}
            </div>

            <div className="mt-14 border-t border-sans_line pt-10">
              <div className="sans-label mb-3">Safety information</div>
              <p className="text-sans_ink leading-relaxed">{e.safety}</p>
            </div>

            {e.references?.length > 0 && (
              <div className="mt-14 border-t border-sans_line pt-10">
                <div className="sans-label mb-3">Scientific references</div>
                <ul className="space-y-2">
                  {e.references.map((r, i) => (
                    <li key={i}>
                      <a href={r.url} target="_blank" rel="noreferrer" className="sans-link text-sans_ink">
                        {r.title} →
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-16 border-t border-sans_line pt-10 text-xs text-sans_mute leading-relaxed">
              Educational content only. This is not medical advice and does not guarantee outcomes.
              Classical Ayurvedic concepts and modern scientific evidence are distinct — we present both for context.
              Please consult a qualified Ayurvedic doctor before using any formulation.
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ClinicalEvidenceList;
